/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import classNames from 'classnames';
import PropTypes from 'prop-types';

// Component imports
import { Avatar, Button, Col, Comment, Empty, Form, Input, List, Popconfirm, Row, Space, Typography } from 'antd';
import { SendIcon } from '../Icons/SendIcon';

// Misc imports
import is from '../../utils/is';
import formatEndUserName from '../../utils/string/formatEndUserName';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useCreateConversationNoteMutation, useDeleteConversationNoteMutation, useUpdateConversationNoteMutation } from '../../store/conversationNotes/conversationNotesApi';
import { useParams, useSearchParams } from 'react-router-dom';
import { DEFAULT_DATETIME_FORMAT } from '../../constants/main';

const ConversationNotesPanel = ({
  hasJoined = false
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { orgId } = useParams();
  const [searchParams] = useSearchParams();

  const { currentUser } = useSelector((state) => state.auth);
  const { currentData: activeConversationInfo } = getCurrentQueryState('fetchActiveConversation', { orgId, conversationId: searchParams.get('conversation') });
  const { currentData: conversationNotes, isSuccess: isFetchConversationNoteDataSuccess } = getCurrentQueryState('fetchConversationNotes', { conversationId: activeConversationInfo?.id }, !activeConversationInfo?.id);
  const [createConversationNote, { isLoading: isCreatingConversationNote }] = useCreateConversationNoteMutation();
  const [updateConversationNote] = useUpdateConversationNoteMutation();
  const [deleteConversationNote] = useDeleteConversationNoteMutation();
  const [noteToEdit, setNoteToEdit] = useState();

  const onEditCancel = () => {
    setNoteToEdit(null);
  };

  const onEditCompleted = async (value) => {
    if (!value.trim() || value.trim() === noteToEdit.text) {
      return setNoteToEdit(null);
    }

    const textToUpdate = {
      text: value.trim()
    };

    const isUpdateSuccessful = await updateConversationNote({ conversationId: activeConversationInfo.id, noteId: noteToEdit.id, data: textToUpdate }).unwrap();

    if (isUpdateSuccessful) {
      setNoteToEdit(null);
    }
  };

  const onCreate = async (values) => {
    values.text = values.text.trim();
    const { text } = values;

    if (!text) {
      return;
    }

    const isCreateSuccessful = await createConversationNote({ conversationId: activeConversationInfo.id, data: values }).unwrap();

    if (isCreateSuccessful) {
      form.resetFields();
    }
  };

  const onDelete = async (noteId) => {
    deleteConversationNote({ conversationId: activeConversationInfo.id, noteId: noteId });
  };

  return (
    <Row
      wrap={false}
      className="flex-column h-100 bg-layout border-radius-base bordered"
    >
      <Col
        flex="auto"
        style={{ height: 300 }}
        className="overflow-y-auto"
      >
        <List
          className="p-2 overflow-y-auto reverse-list"
          locale={{ emptyText: <Empty className="fs-sm" description={t('There are no notes to show')} image={Empty.PRESENTED_IMAGE_SIMPLE}/> }}
          dataSource={is.array(conversationNotes) ? conversationNotes : []}
          loading={!isFetchConversationNoteDataSuccess}
          split={false}
          renderItem={(item) => (
            <List.Item
              key={item.createDate}
              className="p-0"
            >
              <Comment
                className="w-100 mb-0"
                author={
                  <Row
                    wrap={false}
                    gutter={8}
                    className="mb-2"
                    align="middle"
                  >
                    <Col flex="none">
                      <Avatar size={20}>
                        {formatEndUserName(item.from.fullName)}
                      </Avatar>
                    </Col>

                    <Col flex="auto">
                      <Typography.Text
                        ellipsis
                        title={item.from.fullName}
                        className="fw-bold fs-sm"
                      >
                        {item.from.fullName}
                      </Typography.Text>
                    </Col>

                    <Col flex="none">
                      <Typography.Text className="text-muted fs-xs">
                        {moment(item.createDate).format(DEFAULT_DATETIME_FORMAT)}
                      </Typography.Text>
                    </Col>
                  </Row>
                }
                content={
                  <Typography.Paragraph
                    className={classNames('mb-0', {
                      'bg-white p-2 border-radius-base bordered': noteToEdit?.id !== item.id
                    })}
                    editable={{
                      editing: noteToEdit?.id === item.id,
                      icon: <Fragment/>,
                      autoSize: { minRows: 1, maxRows: 4 },
                      onCancel: onEditCancel,
                      onChange: onEditCompleted
                    }}
                    onDoubleClick={() => currentUser.id === item.from.platformUserId && setNoteToEdit(item)}
                  >
                    {item.text}
                  </Typography.Paragraph>
                }
                actions={
                  currentUser.id === item.from.platformUserId ? ([
                    <Space key="actions">
                      <Button
                        key="edit"
                        size="small"
                        type="link"
                        disabled={noteToEdit?.id === item.id}
                        className="fs-sm p-0"
                        onClick={() => setNoteToEdit(item)}
                      >
                        {t('Edit')}
                      </Button>

                      <Popconfirm
                        title={t('Delete this note?')}
                        okText={t('Delete')}
                        placement="bottomLeft"
                        onConfirm={() => onDelete(item.id)}
                      >
                        <Button
                          key="delete"
                          size="small"
                          type="link"
                          className="fs-sm p-0"
                        >
                          {t('Delete')}
                        </Button>
                      </Popconfirm>
                    </Space>
                  ]) : []
                }
              />
            </ List.Item>
          )}
        >
        </List>
      </Col>

      <Col flex="none">
        <Form
          form={form}
          initialValues={{
            text: ''
          }}
          onFinish={onCreate}
        >
          <Row
            className="p-1 bg-white border-top border-radius-bottom-left-base border-radius-bottom-right-base"
            align="middle"
            wrap={false}
          >
            <Col flex="auto">
              <Form.Item
                name="text"
                noStyle
              >
                <Input
                  autoFocus
                  placeholder={hasJoined ? t('Type your note') : t('You must have joined this converstion to write notes')}
                  disabled={!hasJoined}
                  size="large"
                  bordered={false}
                />
              </Form.Item>
            </Col>

            <Col flex="none">
              <Button
                type="link"
                size="large"
                icon={<SendIcon />}
                disabled={!hasJoined}
                loading={isCreatingConversationNote}
                htmlType="submit"
              />
            </Col>
          </Row>
        </Form>
      </Col>
    </Row>
  );
};

ConversationNotesPanel.propTypes = {
  hasJoined: PropTypes.bool
};

export default ConversationNotesPanel;
